/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import React, { useState } from "react";
import { Formik, Form } from "formik";
import {
  PrimaryButton,
  MessageBar,
  MessageBarType,
  Pivot,
  PivotItem,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
} from "@fluentui/react";
import useAxios from "axios-hooks";
import Toast from "../../../Forms/Toast";

const container = css`
  .ms-Pivot {
    margin-bottom: 20px;
  }
  .upload-section {
    margin: 20px 0;
    padding: 20px;
    border: 1px solid #edebe9;
    border-radius: 4px;
  }
  .file-input {
    margin: 10px 0;
  }
`;

const tableContainer = css`
  overflow: auto;
  max-height: 400px;
  margin-top: 10px;
`;

const uploadButton = css`
  margin-top: 10px;
  margin-right: 10px;
`;

const BulkUnitUpload = () => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [selectedTab, setSelectedTab] = useState("backend");
  const [backendData, setBackendData] = useState([]);
  const [userFacingData, setUserFacingData] = useState([]);
  const [backendFile, setBackendFile] = useState(null);
  const [userFacingFile, setUserFacingFile] = useState(null);

  const [{ loading: backendLoading, error: backendError }, executeBackendUpload] = useAxios(
    { url: 'internal/cobrand/bulk-client-unit-upload', method: 'POST' },
    { manual: true }
  );

  const [{ loading: userFacingLoading, error: userFacingError }, executeUserFacingUpload] = useAxios(
    { url: 'internal/cobrand/bulk-unit-upload', method: 'POST' },
    { manual: true }
  );

  // Backend units table columns
  const backendColumns = [
    {
      key: 'clientSchoolID',
      name: 'Client School ID',
      fieldName: 'clientSchoolID',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
    },
    {
      key: 'clientID',
      name: 'Client ID',
      fieldName: 'clientID',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
    },
    {
      key: 'schoolName',
      name: 'School Name',
      fieldName: 'schoolName',
      minWidth: 200,
      maxWidth: 300,
      isResizable: true,
    },
    {
      key: 'coBrandID',
      name: 'CoBrand ID',
      fieldName: 'coBrandID',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
    },
    {
      key: 'ignore',
      name: 'Ignore',
      fieldName: 'ignore',
      minWidth: 80,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: 'displayOnSignup',
      name: 'Display On Signup',
      fieldName: 'displayOnSignup',
      minWidth: 120,
      maxWidth: 150,
      isResizable: true,
    },
  ];

  // User facing units table columns
  const userFacingColumns = [
    {
      key: 'schoolID',
      name: 'School ID',
      fieldName: 'schoolID',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
    },
    {
      key: 'clientSchoolID',
      name: 'Client School ID',
      fieldName: 'clientSchoolID',
      minWidth: 120,
      maxWidth: 180,
      isResizable: true,
    },
    {
      key: 'name',
      name: 'Name',
      fieldName: 'name',
      minWidth: 200,
      maxWidth: 300,
      isResizable: true,
    },
    {
      key: 'platformID',
      name: 'Platform ID',
      fieldName: 'platformID',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
    },
  ];

  const handleBackendUpload = async () => {
   
    if (!backendFile) {
      setToastMessage("Please select a file to upload");
      setShowToast(true);
      return;
    }

    const formData = new FormData();
    formData.append('file', backendFile);

    try {
      const result = await executeBackendUpload({
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (result.data) {
        setBackendData(result.data);
        setToastMessage("Backend units uploaded successfully");
        setShowToast(true);
      }
    } catch (error) {
      console.error("Backend upload failed:", error);
      setToastMessage("Upload failed. Please try again.");
      setShowToast(true);
    }
  };

  const handleUserFacingUpload = async () => {
    if (!userFacingFile) {
      setToastMessage("Please select a file to upload");
      setShowToast(true);
      return;
    }

    const formData = new FormData();
    formData.append('file', userFacingFile);

    try {
      const result = await executeUserFacingUpload({
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (result.data) {
        setUserFacingData(result.data);
        setToastMessage("User facing units uploaded successfully");
        setShowToast(true);
      }
    } catch (error) {
      console.error("User facing upload failed:", error);
      setToastMessage("Upload failed. Please try again.");
      setShowToast(true);
    }
  };

  const handleBackendFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setBackendFile(file);
    } else {
      setToastMessage("Please select a valid CSV file");
      setShowToast(true);
    }
  };

  const handleUserFacingFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setUserFacingFile(file);
    } else {
      setToastMessage("Please select a valid CSV file");
      setShowToast(true);
    }
  };

  return (
    <div css={container}>
      
      <Pivot
        selectedKey={selectedTab}
        onLinkClick={(item) => setSelectedTab(item.props.itemKey)}
      >
        <PivotItem headerText="Back-end units" itemKey="backend">
          {backendError && (
            <MessageBar messageBarType={MessageBarType.error}>
              {backendError.message}
            </MessageBar>
          )}
          
          <div className="upload-section">
            <h3>Upload Back-end Units CSV</h3>
            <p>Select a CSV file to upload back-end unit data.</p>
            
            <div className="file-input">
              <input
                type="file"
                accept=".csv"
                onChange={handleBackendFileChange}
                id="backend-file-input"
              />
              <label htmlFor="backend-file-input">
                {backendFile ? backendFile.name : "No file selected"}
              </label>
            </div>
            
            <PrimaryButton
              text="Upload CSV"
              onClick={handleBackendUpload}
              disabled={backendLoading || !backendFile}
              css={uploadButton}
            />
            <a href='https://igradcdnproduction.blob.core.windows.net/media/backend-units-example.csv' download>
              Download Backend Template
            </a>
            {backendLoading && <span>Uploading...</span>}
          </div>          {backendData.length > 0 && (
            <div>
              <h3>Backend Units Data ({backendData.length} records)</h3>
              <div css={tableContainer}>
                <DetailsList
                  items={backendData}
                  columns={backendColumns}
                  layoutMode={DetailsListLayoutMode.justified}
                  selectionMode={SelectionMode.none}
                  isHeaderVisible={true}
                />
              </div>
            </div>
          )}
        </PivotItem>

        <PivotItem headerText="User facing units" itemKey="userfacing">
          {userFacingError && (
            <MessageBar messageBarType={MessageBarType.error}>
              {userFacingError.message}
            </MessageBar>
          )}
          
          <div className="upload-section">
            <h3>Upload User Facing Units CSV</h3>
            <p>Select a CSV file to upload user facing unit data.</p>
            
            <div className="file-input">
              <input
                type="file"
                accept=".csv"
                onChange={handleUserFacingFileChange}
                id="userfacing-file-input"
              />
              <label htmlFor="userfacing-file-input">
                {userFacingFile ? userFacingFile.name : "No file selected"}
              </label>
            </div>
            
            <PrimaryButton
              text="Upload CSV"
              onClick={handleUserFacingUpload}
              disabled={userFacingLoading || !userFacingFile}
              css={uploadButton}
            />
             <a href='https://igradcdnproduction.blob.core.windows.net/media/user-facing-units-example.csv' download>
              Download User Facing Template
            </a>
            {userFacingLoading && <span>Uploading...</span>}
          </div>          {userFacingData.length > 0 && (
            <div>
              <h3>User Facing Units Data ({userFacingData.length} records)</h3>
              <div css={tableContainer}>
                <DetailsList
                  items={userFacingData}
                  columns={userFacingColumns}
                  layoutMode={DetailsListLayoutMode.justified}
                  selectionMode={SelectionMode.none}
                  isHeaderVisible={true}
                />
              </div>
            </div>
          )}
        </PivotItem>
      </Pivot>

      <Toast 
        show={showToast} 
        message={toastMessage} 
        onClose={() => setShowToast(false)} 
      />
    </div>
  );
};

export default BulkUnitUpload;
